import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import * as _ from 'lodash';
import { MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { TabViewModule } from "primeng/tabview";
import { AppSettings } from "../../../shared/app.settings";
import { MiFormComponent } from "../../../shared/components/mi-form/mi-form.component";
import type { Country } from "../../../shared/models/country";
import type { Language } from "../../../shared/models/language";
import { CommonBindingDataService } from "../../../shared/services/common-binding-data.service";
import { EntityService } from "../../../shared/services/entity.service";
import type { AttributeData } from "../../models/attribute.models";

import { AccessProviderDirective } from "app/modules/shared/directives/access-provider.directive";
import { ConfigService } from "app/modules/shared/services/config.service";
import type { EntityResponse } from "../../models/entityResponse.models";
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';

interface RelatedDataItem {
  entityCode: string;
  entityId: string;
  actionStatus: string;
  [key: string]: unknown;
}

interface OrganizedData {
  [key: string]: Record<string, unknown>[];
}

interface TabChangeEvent {
  index: number;
  originalEvent: {
    target: {
      innerText: string;
    };
  };
}

@Component({
  selector: 'app-add-trial',
  standalone: true,
  imports: [
    BreadcrumbModule,
    TabViewModule,
    MiFormComponent,
    DialogModule,
    ButtonModule,
    TranslateModule,
    AccessProviderDirective
  ],
  templateUrl: './add-trial.component.html',
  styleUrl: './add-trial.component.scss'
})
export class AddTrialComponent implements OnInit {

  @ViewChild(MiFormComponent) miFormComponent: MiFormComponent;
  activeIndex: number = 0;
  data: AttributeData;
  protocolAttributeData: AttributeData;
  routePath: MenuItem[] = [];
  attributeLabels: Record<string, string> = {};
  moduleName: string;
  country: Country;
  language: Language;
  relation: string = "oneToOne";
  cols: Array<{ field: string; header: string }> = [];
  protocolAttributeLength: number;
  protocolId: string;
  protocolForDocument: string;
  protocolDocumentId: string;
  attributeValues: Record<string, unknown> = {};
  nextBtnLabel: string;
  previousBtnLabel: string;
  label: string;
  initialLabel: string = this.cs.getLabel("lbl_details");
  saveProtocolBtnLabel: string = this.cs.getLabel("protocol.lbl_save_protocol");
  isFirstTabSaved: boolean = false;
  isNew: boolean = true;
  prefixToAttachForButtonIdToUniquelyIdentifyButton = AppSettings.BUTTON_PREFIX
  tabCode: string;
  tabIndex: number;
  isEdit: boolean = false;
  loadDialogView = false;
  protocolHeader: string;
  successMessage: string;
  sponsorRelation: { id: string; name: string; entityRelationshipConfigId?: string };
  sponsorStatusAttributeId: number;

  constructor(
    private protocolService: EntityService,
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private metaDataService: MetaDataService
  ) { }

  ngOnInit() {
    this.retrieveProtocolId();
    this.initializeLabels();
    this.initializeProtocolService();
    this.loadProtocolData();
    this.setRoutePath('details');
    this.isFirstTabSaved = false;
    this.activeIndex = 0;
  }

  private initializeLabels() {
    if (this.protocolId) {
      this.protocolHeader = this.cs.getLabel("protocol.lbl_update_protocol");
      this.nextBtnLabel = this.cs.getLabel("protocol.lbl_update_protocol");
      this.successMessage = this.cs.getLabel('protocol.lbl_protocol_updated_successfully')
      this.saveProtocolBtnLabel = this.cs.getLabel("protocol.lbl_update_protocol");
    } else {
      this.protocolHeader = this.cs.getLabel("protocol.lbl_save_protocol");
      this.nextBtnLabel = this.cs.getLabel("protocol.lbl_save_protocol");
      this.successMessage = this.cs.getLabel('protocol.lbl_protocol_added_successfully')
      this.saveProtocolBtnLabel = this.cs.getLabel("protocol.lbl_save_protocol");
    }
    this.previousBtnLabel = this.cs.getLabel("lbl_cancel");
  }

  private retrieveProtocolId() {
    this.protocolId = this.route.snapshot.paramMap.get("id");
    if (this.protocolId) {
      this.protocolForDocument = this.protocolId;
      this.isEdit = true;
    }
  }

  private initializeProtocolService() {
    this.moduleName = AppSettings.ENTITY_TYPE.PROTOCOL;
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.protocolService.setRelatedData([]);
  }

  private loadProtocolData() {
    if (this.protocolId) {
      this.protocolService
        .getEntity(this.protocolId, AppSettings.ENTITY_CODE.PROTOCOL, AppSettings.VIEW_CODE.ADD_EDIT_VIEW)
        .subscribe((res) => {
          if (res) {
            this.processProtocolResponse(res as EntityResponse);
            this.isNew = false;
            const currentTab = this.protocolAttributeData?.tabs[this.activeIndex];
            if (currentTab) {
              this.setRoutePath(currentTab.tabCode);
            }
          }
        });
    } else {
      this.getAttributes();
    }
  }

  private processProtocolResponse(data: EntityResponse) {
    this.attributeValues = this.cs.getOrgAttributeValues(data) as unknown as Record<string, unknown>;
    this.normalizeStartEndDate();
    this.processRelatedData(data.relatedData as unknown as RelatedDataItem[]);
    this.getAttributes();
  }

  private normalizeStartEndDate() {
    const startEndDateCodes = ['start_date', 'end_date'];
    for (const dateCode of startEndDateCodes) {
      if (this.attributeValues[dateCode]) {
        const updatedDate = this.metaDataService.setDateInTimezone(this.attributeValues[dateCode] as number);
        this.attributeValues[dateCode] = new Date(updatedDate as string | number);
      }
    }
  }

  private processRelatedData(relatedData: RelatedDataItem[]) {
    if (relatedData.length > 0) {
      const organizedData: OrganizedData = {};
      relatedData.forEach(item => {
        if (item.actionStatus === "DELETED") {
          return;
        }
        const entityCode = item.entityCode;
        if (!organizedData[entityCode]) {
          organizedData[entityCode] = [];
        }
        const entityData = this.cs.getOrgAttributeValues(item) as unknown as Record<string, unknown>;
        entityData.entity_name = entityData.sponsor_name;
        entityData.entityId = item.entityId;
        organizedData[entityCode].push(entityData);
      });
      this.protocolService.setRelatedData(organizedData);
    }
  }

  getAttributes() {
    const entityType = AppSettings.ENTITY_TYPE.PROTOCOL;
    const viewCode = AppSettings.VIEW_CODE.ADD_EDIT_VIEW;

    this.protocolService.getAttributeDefinition(AppSettings.ENTITY_CODE.SPONSOR, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW)
      .subscribe((filterResponse: unknown) => {
        if (filterResponse) {
          const sponsorStatus = this.cs.getOrganizedAttribute(filterResponse)?.tabs[0]?.groups[0]?.fields?.find(
            (element: { attributeCode: string }) => element.attributeCode === 'sponsor_status')?.attributeId;
          if (sponsorStatus) {
            this.sponsorStatusAttributeId = sponsorStatus;
           this.protocolService.getAttributeDefinition(entityType, viewCode).subscribe(res => {
            if (!res) return;
            this.setProtocolView(res);
            const currentTab = this.protocolAttributeData.tabs[this.activeIndex];
            if (currentTab) {
              this.setRoutePath(currentTab.tabCode);
            }
          });
          }
        }
    });
  }

  setProtocolView(res: AttributeData) {
    this.loadDialogView = true;
    this.attributeLabels = this.cs.getAttributeLabels(res) as unknown as Record<string, string>;
    this.protocolAttributeData = this.cs.getOrganizedAttribute(res);
    this.protocolAttributeData.tabs = this.cs.setDependsOnAttributeCode(this.protocolAttributeData?.tabs);
    this.sponsorRelation = _.find(res?.relations, { otherEntityCode: 'sponsor', relation: "oneToOne" });

    this.setDateFieldValidations();
    this.protocolAttributeLength = this.protocolAttributeData.tabs.length;


    this.protocolAttributeData.tabs = this.protocolAttributeData.tabs.map(tab => ({
      ...tab,
      groups: tab.groups.map(group => ({
        ...group,
        fields: group.fields.filter((field: any) => {
          if (field.attributeCode === 'ctn_file_upload') {
            field.documentURLs = this.attributeValues.ctn_file_upload_url;
          }
          if (field.attributeCode === 'sponsor_entity_id') {
            field.moduleName = AppSettings.ENTITY_CODE.SPONSOR;
            field.disabled = this.protocolId ? true : false;
          }
          if (field.attributeCode === 'sponsor_entity_id') {
            field.moduleName = AppSettings.ENTITY_CODE.SPONSOR;
            if (this.protocolId) {
              field.disabled = true;
            }
            field.filter = [{
              attributeId: this.sponsorStatusAttributeId,
              attributeValue: "Active"
            }];
            return true;
          }
          return true;
        })
      }))
    }));
  }

  private setDateFieldValidation(tabCode, groupCode, attributeCode, isMax) {
    const tab = this.protocolAttributeData.tabs.find(ele => ele.tabCode === tabCode);
    const group = tab?.groups.find(ele => ele.code === groupCode);
    const field = group?.fields.find(ele => ele.attributeCode === attributeCode);

    if (field) {
      field.validation[isMax ? 'max' : 'min'] = new Date().getTime();
    }
  }

  private setDateFieldValidations() {
    this.setDateFieldValidation('details', 'additional_details', AppSettings.DATE_ATTRIBUTE_IDS.PROTOCOL_START_DATE, true);
    this.setDateFieldValidation('details', 'additional_details', AppSettings.DATE_ATTRIBUTE_IDS.PROTOCOL_END_DATE, false);
  }

  setRoutePath(tabCode: string) {
    const routeLabel = this.protocolId ? this.cs.getLabel("protocol.lbl_edit_protocol") : this.cs.getLabel("protocol.lbl_add_new_protocol");
    if (!tabCode && this.protocolAttributeData?.tabs?.length > 0) {
      tabCode = this.protocolAttributeData.tabs[0].tabCode;
    }

    this.routePath = [
      {
        label: `${routeLabel}`,
        routerLink: this.protocolId ? '../../list' : '../list',
        command: () => {
          if (window.history.length > 1) {
            window.history.go(-1);
          } else {
            this.router.navigate(["/app/trials"]);
          }
        },
        icon: "pi pi-arrow-left",
        iconStyle: { "font-weight": "bold", "margin-right": "10px" },
      }
    ];
    if (tabCode) {
      this.routePath.push({
        label: `${tabCode}`,
        styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
        style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
      });
    }
  }

  onSaveProtocol(event) {
    const protocolData = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: AppSettings.ENTITY_CODE.PROTOCOL,
      countryCode: this.country[0].countryCode,
      languageCode: this.language[0].langCode,
      data: [],
      relationshipData: []
    };

    const vData = event;
    const dateIds = ["start_date", "end_date"];
    for (const [key, value] of Object.entries(vData)) {
      let attributeValue = <any>value;
      if (dateIds.includes(key)) {
        const dateValue = this.metaDataService.getTimeZoneFormattedDate(value as Date);
        if(key === 'end_date') {
          attributeValue = new Date(new Date(dateValue).setHours(23, 59, 59, 999)).getTime();
        } else {
          attributeValue = new Date(dateValue).getTime();
        }
        protocolData.data.push({
          attributeCode: key,
          attributeValue
        });
      } else if (key === 'sponsor_entity_id') {
        protocolData?.relationshipData?.push({
          entityRelationshipConfigId: this.sponsorRelation.entityRelationshipConfigId,
          otherEntityId: value
        });
        protocolData.data.push({
          attributeCode: key,
          attributeValue,
        });
      } else if (attributeValue || attributeValue?.length) {
        protocolData.data.push({
          attributeCode: key,
          attributeValue
        });
      }
    }
    const entitiesData = {
      countryCode: this.country[0].countryCode,
      tenantCode: this.configService.getForTenantCode(),
    };

    if (!this.protocolId) {
      this.protocolService
        .createEntities(AppSettings.ENTITY_CODE.PROTOCOL, entitiesData)
        .subscribe((res: EntityResponse) => {
          this.protocolId = res.entityId;
          if (this.protocolId) {
            this.protocolForDocument = this.protocolId;
          }
          this.saveProtocolData(protocolData, true);
        });
    } else {
      this.saveProtocolData(protocolData, false);
    }
  }

  saveProtocolData(protocolData, isNew: boolean) {
    this.isNew = isNew;
    protocolData.data = this.cs.mapAttributeIds(protocolData.data, this.protocolAttributeData.tabs)
    this.protocolService
      .saveAttributeData(protocolData.entityCode, this.protocolId, protocolData).subscribe({
        next: () => {
          this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.successMessage });
          this.redirectToListPage();
        },
        error: () => {
          if (isNew) {
            this.cs.onDisplayMessage(this.data, 'protocol', AppSettings.MSG_ACTION.ADD, 'error');
          } else {
            this.cs.onDisplayMessage(this.data, 'protocol', AppSettings.MSG_ACTION.UPDATE, 'error');
          }
        }
      });
  }

  onPreviousClick() {
  }

  onNextBtnClick() {
    if (this.protocolAttributeData.tabs && this.protocolAttributeData.tabs.length > 0) {
      const currentTab = this.protocolAttributeData.tabs[this.activeIndex];
      this.setRoutePath(currentTab.tabCode);
      if (currentTab) {
        const btn = document.getElementById(`0_${currentTab.tabCode}`);
        if (btn) {
          btn.click();
        }
      }
    }
  }

  onTabChange(event: TabChangeEvent) {
    const currentTab = this.protocolAttributeData.tabs[this.activeIndex];
    if (currentTab) {
      this.setRoutePath(currentTab.tabCode);
    }
    if (event.index === 0) {
      this.isFirstTabSaved = false;
    }
    if (!this.isFirstTabSaved && event.index > 0) {
      this.activeIndex = 0;
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please save details in first tab before proceeding'
      });
      return;
    }

    this.label = event.originalEvent.target.innerText.trim();
  }

  getLastIndex(): number {
    return this.protocolAttributeLength - 1;
  }

  isLastIndex(): boolean {
    return this.activeIndex === this.getLastIndex();
  }

  onCancel() {
    this.isFirstTabSaved = false;
    this.miFormComponent.resetForm();
    this.redirectToListPage();
  }

  redirectToListPage() {
    if (window.history.length > 1) {
      window.history.go(-1);
    } else {
      this.router.navigate(["/app/trials"]);
    }
  }


}

